import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus, Trash2, Edit2, X, Loader2, RefreshCw, CalendarDays, Star, Users, Download,
  ChevronRight, ChevronDown, MoreHorizontal, List, LayoutGrid, Check,
  AlertTriangle, Globe, MapPin,
} from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import {
  cx, fullName, Button, IconButton, Chip, Avatar, AvatarStack, Toggle, Segmented, SearchInput,
  Menu, Modal, ModalHeader, ConfirmProvider, useConfirm, UIStyles,
  selectClass, inputClass, labelClass,
} from './settingsUI';

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR + i);
const YEAR_RANGE = `${YEARS[0]}–${YEARS[YEARS.length - 1]}`;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const POPULAR = ['GB', 'IE', 'US', 'ZA', 'IN', 'AU', 'CA', 'DE', 'FR', 'ES', 'NL', 'AE'];

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function unwrapList(res, key) {
  if (Array.isArray(res)) return res;
  const d = res?.data ?? res;
  if (Array.isArray(d)) return d;
  if (key && Array.isArray(d?.[key])) return d[key];
  return [];
}

function unwrapObj(res) {
  return res?.data ?? res ?? {};
}

const pad = n => String(n).padStart(2, '0');
const toISO = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const isoOf = value => String(value || '').slice(0, 10);

function parseISO(value) {
  const [y, m, d] = isoOf(value).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

const todayISO = () => toISO(new Date());

function daysFromToday(iso) {
  return Math.round((parseISO(iso) - parseISO(todayISO())) / 86400000);
}

function relativeLabel(iso) {
  const n = daysFromToday(iso);
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  if (n === -1) return 'Yesterday';
  if (n < 0) return `${Math.abs(n)} days ago`;
  if (n < 45) return `in ${n} days`;
  const months = Math.round(n / 30.4);
  return `in ${months} month${months === 1 ? '' : 's'}`;
}

const formatDate = (iso, opts) => parseISO(iso).toLocaleDateString('en-GB', opts);
const fmtLong  = iso => formatDate(iso, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const fmtShort = iso => formatDate(iso, { weekday: 'short', day: 'numeric', month: 'short' });
const weekdayLong = iso => formatDate(iso, { weekday: 'long' });

function isWeekend(iso) {
  const day = parseISO(iso).getDay();
  return day === 0 || day === 6;
}

function flagEmoji(code = '') {
  if (!/^[A-Z]{2}$/i.test(code)) return '🏳️';
  return String.fromCodePoint(...code.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0)));
}

function splitLocationName(name = '') {
  const i = name.indexOf(': ');
  return i === -1 ? { country: name, region: null } : { country: name.slice(0, i), region: name.slice(i + 2) };
}

// ─────────────────────────────────────────────────────────────
// Local pieces
// ─────────────────────────────────────────────────────────────
function DefaultBadge() {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-ink-50 px-2 py-0.5 text-[11px] font-semibold text-ink">
      <Star size={10} className="fill-current" /> Default
    </span>
  );
}

// ═════════════════════════════════════════════════════════════
// PAGE
// ═════════════════════════════════════════════════════════════
function PublicHolidaysPage() {
  return (
    <ConfirmProvider>
      <PublicHolidaysContent />
      <UIStyles />
    </ConfirmProvider>
  );
}

function PublicHolidaysContent() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [openId, setOpenId]       = useState(null);
  const [showAdd, setShowAdd]     = useState(false);

  const loadLocations = useCallback(async () => {
    try {
      const res = await apiService.getHolidayLocations(CURRENT_YEAR);
      setLocations(unwrapList(res, 'locations'));
    } catch {
      toast.error('Failed to load public holiday locations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLocations(); }, [loadLocations]);

  const openLocation = locations.find(l => l.id === openId);

  const nextOverall = useMemo(() => {
    const upcoming = locations
      .filter(l => l.nextHoliday?.date)
      .map(l => ({ ...l.nextHoliday, date: isoOf(l.nextHoliday.date), location: l }))
      .sort((a, b) => a.date.localeCompare(b.date));
    if (!upcoming.length) return null;
    const first = upcoming[0];
    return { ...first, others: upcoming.filter(u => u.date === first.date).length - 1 };
  }, [locations]);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3 border-b border-sand-200 pb-5">
        <div className="min-w-0">
          <h2 className="m-0 text-xl font-semibold tracking-tight text-sand-900">Public holidays</h2>
          <p className="m-0 mt-1 max-w-xl text-sm leading-relaxed text-sand-600">
            Each location has its own holiday calendar. People get the public holidays of the location they belong to.
          </p>
        </div>
        <Button variant="primary" icon={Plus} onClick={() => setShowAdd(true)}>Add location</Button>
      </div>

      {/* Next holiday banner */}
      {nextOverall && <NextHolidayBanner next={nextOverall} onOpen={() => setOpenId(nextOverall.location.id)} />}

      {/* Location cards */}
      <div className="grid gap-4 sm:grid-cols-2">
        {loading ? (
          [0, 1].map(i => <SkeletonCard key={i} />)
        ) : (
          <>
            {locations.map(loc => (
              <LocationCard key={loc.id} location={loc} onOpen={() => setOpenId(loc.id)} />
            ))}
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="group flex min-h-[188px] flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-sand-200 p-5 text-center transition-all hover:border-ink/40 hover:bg-ink-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sand-100 text-sand-600 transition-all group-hover:scale-110 group-hover:bg-ink group-hover:text-[color:var(--on-accent)]">
                <Plus size={20} />
              </span>
              <span>
                <span className="block text-sm font-semibold text-sand-900">Add a location</span>
                <span className="block text-[13px] text-sand-600">Import holidays for another country or region</span>
              </span>
            </button>
          </>
        )}
      </div>

      {!loading && locations.length > 0 && (
        <p className="m-0 mt-4 flex items-center gap-1.5 text-xs text-sand-500">
          <Star size={11} /> People who aren't assigned to a location use the default one.
        </p>
      )}

      {showAdd && (
        <AddLocationModal
          existing={locations}
          onClose={() => setShowAdd(false)}
          onCreated={async (id) => {
            setShowAdd(false);
            await loadLocations();
            setOpenId(id);
          }}
        />
      )}

      {openLocation && (
        <LocationModal
          location={openLocation}
          locations={locations}
          onClose={() => setOpenId(null)}
          onChanged={loadLocations}
          onDeleted={() => { setOpenId(null); loadLocations(); }}
        />
      )}
    </div>
  );
}

function NextHolidayBanner({ next, onOpen }) {
  const days = daysFromToday(next.date);
  const d = parseISO(next.date);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="ph-fade group mb-5 flex w-full items-center gap-4 rounded-2xl bg-ink-50 p-4 text-left transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 sm:p-5"
    >
      <span className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl bg-ink text-[color:var(--on-accent)] shadow-sm">
        <span className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{MONTHS[d.getMonth()].slice(0, 3)}</span>
        <span className="text-xl font-bold leading-none">{d.getDate()}</span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink/70">Next public holiday</span>
        <span className="block truncate text-base font-semibold text-sand-900">{next.name}</span>
        <span className="block truncate text-[13px] text-sand-600">
          {fmtShort(next.date)} · {flagEmoji(next.location.countryCode)} {next.location.name}
          {next.others > 0 && ` +${next.others} more`}
        </span>
      </span>
      <span className="hidden shrink-0 flex-col items-end sm:flex">
        <span className="text-2xl font-bold leading-none text-ink">{days <= 0 ? 'Today' : days}</span>
        <span className="mt-1 text-xs font-medium text-sand-600">{days <= 0 ? 'Today' : days === 1 ? 'day to go' : 'days to go'}</span>
      </span>
    </button>
  );
}

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-2xl border border-sand-200 p-5">
      <div className="flex gap-3">
        <div className="h-12 w-12 rounded-xl bg-sand-100" />
        <div className="flex-1 space-y-2 py-1">
          <div className="h-4 w-2/3 rounded bg-sand-100" />
          <div className="h-3 w-1/3 rounded bg-sand-100" />
        </div>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="h-12 rounded-xl bg-sand-100" />
        <div className="h-12 rounded-xl bg-sand-100" />
      </div>
      <div className="mt-4 h-4 w-1/2 rounded bg-sand-100" />
    </div>
  );
}

function LocationCard({ location, onOpen }) {
  const { country, region } = splitLocationName(location.name);
  const next = location.nextHoliday?.date ? { ...location.nextHoliday, date: isoOf(location.nextHoliday.date) } : null;
  const empty = !location.holidayCount;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="ph-fade group flex flex-col rounded-2xl border border-sand-200 bg-[color:var(--surface)] p-5 text-left transition-all hover:-translate-y-0.5 hover:border-sand-300 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sand-50 text-2xl ring-1 ring-sand-200">
          {flagEmoji(location.countryCode)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="truncate text-[15px] font-semibold text-sand-900">{country}</span>
            {location.isDefault && <DefaultBadge />}
          </span>
          <span className="mt-0.5 flex items-center gap-1 text-[13px] text-sand-600">
            <MapPin size={12} className="shrink-0" />
            <span className="truncate">{region || 'National holidays'}</span>
          </span>
        </span>
        <ChevronRight size={18} className="mt-1 shrink-0 text-sand-300 transition-all group-hover:translate-x-0.5 group-hover:text-sand-600" />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-sand-50 px-3 py-2.5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-sand-500">Days in {CURRENT_YEAR}</div>
          <div className="mt-0.5 text-lg font-semibold text-sand-900">{location.holidayCount || 0}</div>
        </div>
        <div className="rounded-xl bg-sand-50 px-3 py-2.5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-sand-500">People</div>
          <div className="mt-0.5 flex items-center justify-between gap-2">
            <span className="text-lg font-semibold text-sand-900">{location.peopleCount || 0}</span>
            <AvatarStack members={location.members} max={3} />
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 border-t border-sand-100 pt-3 text-[13px]">
        {next ? (
          <>
            <CalendarDays size={14} className="shrink-0 text-ink" />
            <span className="min-w-0 flex-1 truncate text-sand-700">
              Next: <span className="font-medium text-sand-900">{next.name}</span>
            </span>
            <Chip tone={daysFromToday(next.date) <= 7 ? 'green' : 'sand'}>{relativeLabel(next.date)}</Chip>
          </>
        ) : empty ? (
          <>
            <AlertTriangle size={14} className="shrink-0 text-amber-600" />
            <span className="text-[color:var(--warn-text)]">No holidays yet — open to import</span>
          </>
        ) : (
          <>
            <CalendarDays size={14} className="shrink-0 text-sand-500" />
            <span className="text-sand-600">No upcoming holidays imported</span>
          </>
        )}
      </div>
    </button>
  );
}

// ═════════════════════════════════════════════════════════════
// MODAL: Add location
// ═════════════════════════════════════════════════════════════
function AddLocationModal({ existing = [], onClose, onCreated }) {
  const [countries, setCountries]               = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [query, setQuery]                       = useState('');
  const [activeIndex, setActiveIndex]           = useState(0);
  const [countryCode, setCountryCode]           = useState('');
  const [regions, setRegions]                   = useState([]);
  const [loadingRegions, setLoadingRegions]     = useState(false);
  const [region, setRegion]                     = useState('');
  const [importAll, setImportAll]               = useState(true);
  const [saving, setSaving]                     = useState(false);
  const [savingStep, setSavingStep]             = useState('');
  const listRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiService.getHolidayCountries();
        setCountries(unwrapList(res, 'countries'));
      } catch {
        toast.error('Failed to load countries');
      } finally {
        setLoadingCountries(false);
      }
    })();
  }, []);

  useEffect(() => {
    setRegion('');
    setRegions([]);
    if (!countryCode) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingRegions(true);
      try {
        const list = unwrapList(await apiService.getHolidayCountryRegions(countryCode), 'regions');
        if (cancelled) return;
        setRegions(list);
        if (countryCode === 'GB' && list.some(r => r.code === 'GB-ENG')) setRegion('GB-ENG');
      } catch {
        /* regions are optional */
      } finally {
        if (!cancelled) setLoadingRegions(false);
      }
    })();
    return () => { cancelled = true; };
  }, [countryCode]);

  const existingCodes = useMemo(() => new Set(existing.map(l => l.countryCode)), [existing]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q);
  }, [countries, query]);

  const popular = useMemo(
    () => POPULAR.map(code => countries.find(c => c.code === code)).filter(Boolean),
    [countries],
  );

  useEffect(() => { setActiveIndex(0); }, [query]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${activeIndex}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const selected = countries.find(c => c.code === countryCode);
  const duplicate = !!countryCode && existing.some(l => l.countryCode === countryCode && (l.region || '') === region);

  function handleSearchKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, filtered.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && filtered[activeIndex]) { e.preventDefault(); setCountryCode(filtered[activeIndex].code); }
  }

  async function handleSave() {
    if (!countryCode || duplicate) return;
    setSaving(true);
    try {
      setSavingStep('Creating location…');
      const created = unwrapObj(await apiService.createHolidayLocation({ countryCode, region: region || null }));

      if (importAll && created?.id) {
        setSavingStep(`Importing ${YEAR_RANGE}…`);
        try {
          const result = unwrapObj(await apiService.importLocationHolidays(created.id, YEARS));
          toast.success(`Location added with ${result.imported ?? 0} holidays`);
        } catch (err) {
          toast.error(err?.response?.data?.message || 'Location added, but importing holidays failed');
        }
      } else {
        toast.success('Location added');
      }
      onCreated(created.id);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to add location');
    } finally {
      setSaving(false);
      setSavingStep('');
    }
  }

  return (
    <Modal onClose={saving ? undefined : onClose} className="max-w-lg" z={300} labelledBy="ph-add-title">
      <ModalHeader
        id="ph-add-title"
        title="Add location"
        subtitle={selected ? 'Choose a region and how to set it up' : 'Pick the country whose public holidays you need'}
        onClose={saving ? undefined : onClose}
        leading={
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ink-50 text-ink">
            <Globe size={19} />
          </span>
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-2 pt-3 sm:px-6">
        {!selected ? (
          <>
            <SearchInput
              value={query}
              onChange={setQuery}
              onKeyDown={handleSearchKey}
              placeholder={loadingCountries ? 'Loading countries…' : `Search ${countries.length} countries…`}
              autoFocus
            />

            {!query && popular.length > 0 && (
              <div className="mt-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-sand-500">Popular</div>
                <div className="flex flex-wrap gap-2">
                  {popular.map(c => (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => setCountryCode(c.code)}
                      className="inline-flex h-9 items-center gap-2 rounded-full border border-sand-200 bg-[color:var(--surface)] px-3 text-[13px] font-medium text-sand-700 transition-all hover:-translate-y-px hover:border-ink/40 hover:bg-ink-50 hover:text-ink"
                    >
                      <span className="text-base">{flagEmoji(c.code)}</span>
                      {c.name}
                      {existingCodes.has(c.code) && <Check size={12} className="text-[color:var(--ok-text)]" />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-2 mt-5 text-[11px] font-semibold uppercase tracking-wider text-sand-500">
              {query ? `${filtered.length} result${filtered.length === 1 ? '' : 's'}` : 'All countries'}
            </div>
            <div ref={listRef} className="max-h-64 overflow-y-auto rounded-xl border border-sand-200 p-1">
              {loadingCountries ? (
                <div className="flex items-center justify-center py-10 text-sand-500"><Loader2 size={20} className="animate-spin" /></div>
              ) : filtered.length === 0 ? (
                <div className="py-10 text-center text-sm text-sand-500">No countries match “{query}”</div>
              ) : (
                filtered.map((c, i) => (
                  <button
                    key={c.code}
                    type="button"
                    data-index={i}
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => setCountryCode(c.code)}
                    className={cx(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                      i === activeIndex ? 'bg-ink-50 text-ink' : 'text-sand-900',
                    )}
                  >
                    <span className="text-lg">{flagEmoji(c.code)}</span>
                    <span className="min-w-0 flex-1 truncate font-medium">{c.name}</span>
                    {existingCodes.has(c.code) && <Chip tone="green"><Check size={10} /> Added</Chip>}
                    <span className="text-xs font-semibold text-sand-500">{c.code}</span>
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="ph-fade space-y-5">
            {/* Selected country */}
            <div className="flex items-center gap-3 rounded-2xl border border-sand-200 bg-sand-50 p-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[color:var(--surface)] text-2xl ring-1 ring-sand-200">{flagEmoji(selected.code)}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold text-sand-900">{selected.name}</span>
                <span className="block text-xs text-sand-600">Country code {selected.code}</span>
              </span>
              <Button size="sm" variant="ghost" disabled={saving} onClick={() => setCountryCode('')}>Change</Button>
            </div>

            {/* Region */}
            {(loadingRegions || regions.length > 0) && (
              <div>
                <label className={labelClass}>Region</label>
                {loadingRegions ? (
                  <div className="flex h-10 items-center gap-2 text-sm text-sand-500"><Loader2 size={14} className="animate-spin" /> Loading regions…</div>
                ) : regions.length <= 8 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {[{ code: '', name: 'Whole country' }, ...regions].map(r => {
                      const on = region === r.code;
                      return (
                        <button
                          key={r.code || 'all'}
                          type="button"
                          disabled={saving}
                          onClick={() => setRegion(r.code)}
                          className={cx(
                            'flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition-all',
                            on ? 'border-ink bg-ink-50 text-ink ring-2 ring-ink/10' : 'border-sand-200 text-sand-700 hover:border-sand-300 hover:bg-sand-50',
                          )}
                        >
                          <span className={cx('flex h-4 w-4 shrink-0 items-center justify-center rounded-full border', on ? 'border-ink bg-ink text-[color:var(--on-accent)]' : 'border-sand-300')}>
                            {on && <Check size={10} />}
                          </span>
                          <span className="truncate">{r.name}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <select value={region} onChange={e => setRegion(e.target.value)} disabled={saving} className={selectClass}>
                    <option value="">Whole country (national holidays only)</option>
                    {regions.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
                  </select>
                )}
                <p className="m-0 mt-2 text-xs text-sand-500">A region adds its own holidays on top of the national ones.</p>
              </div>
            )}

            {/* Import toggle */}
            <div className="flex items-center justify-between gap-4 rounded-2xl border border-sand-200 p-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-sand-900">Import official holidays</div>
                <div className="text-[13px] text-sand-600">Fills in {YEAR_RANGE} automatically. You can edit any date afterwards.</div>
              </div>
              <Toggle checked={importAll} onChange={setImportAll} disabled={saving} />
            </div>

            {duplicate && (
              <div className="flex items-center gap-2 rounded-xl bg-[color:var(--warn-bg)] px-3 py-2.5 text-[13px] text-[color:var(--warn-text)]">
                <AlertTriangle size={14} className="shrink-0" /> You already have this location.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 border-t border-sand-100 px-5 py-4 sm:px-6">
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" icon={Plus} loading={saving} disabled={!countryCode || duplicate} onClick={handleSave}>
          {saving ? savingStep : 'Add location'}
        </Button>
      </div>
    </Modal>
  );
}

// ═════════════════════════════════════════════════════════════
// MODAL: Location detail
// ═════════════════════════════════════════════════════════════
function LocationModal({ location, locations = [], onClose, onChanged, onDeleted }) {
  const confirm = useConfirm();
  const [tab, setTab]             = useState('holidays');
  const [year, setYear]           = useState(CURRENT_YEAR);
  const [view, setView]           = useState('list');
  const [holidays, setHolidays]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [importing, setImporting] = useState(null); // null | 'year' | 'all'
  const [editor, setEditor]       = useState(null); // null | { holiday } | { date }
  const [search, setSearch]       = useState('');
  const [busy, setBusy]           = useState(false);

  const { country, region } = splitLocationName(location.name);

  const loadHolidays = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.getLocationHolidays(location.id, year);
      setHolidays(unwrapList(res, 'holidays'));
    } catch {
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, [location.id, year]);

  useEffect(() => { loadHolidays(); }, [loadHolidays]);

  const afterChange = () => { loadHolidays(); onChanged?.(); };

  const nextId = useMemo(() => {
    const t = todayISO();
    return holidays.find(h => isoOf(h.date) >= t)?.id;
  }, [holidays]);

  const weekdayCount = useMemo(() => holidays.filter(h => !isWeekend(isoOf(h.date))).length, [holidays]);

  async function handleDelete(holiday) {
    const ok = await confirm({
      title: `Delete ${holiday.name}?`,
      message: `${fmtLong(isoOf(holiday.date))} will no longer be a public holiday for people in ${location.name}.`,
      confirmLabel: 'Delete holiday',
      tone: 'danger',
    });
    if (!ok) return;
    const previous = holidays;
    setHolidays(list => list.filter(h => h.id !== holiday.id)); // optimistic
    try {
      await apiService.deleteLocationHoliday(location.id, holiday.id);
      toast.success('Holiday deleted');
      onChanged?.();
    } catch {
      setHolidays(previous);
      toast.error('Failed to delete holiday');
    }
  }

  async function handleImport(mode) {
    const years = mode === 'all' ? YEARS : [year];
    const label = mode === 'all' ? YEAR_RANGE : String(year);
    const ok = await confirm({
      title: `Import holidays for ${label}?`,
      message: `Official public holidays for ${location.name} will be added. Dates you already have are kept as they are.`,
      confirmLabel: 'Import',
    });
    if (!ok) return;
    setImporting(mode);
    try {
      const { imported = 0, skipped = 0, byYear = {} } = unwrapObj(await apiService.importLocationHolidays(location.id, years));
      const failed = Object.entries(byYear).filter(([, v]) => v?.error).map(([y]) => y);
      if (imported) toast.success(`Imported ${imported} holiday${imported === 1 ? '' : 's'}${skipped ? ` · ${skipped} already there` : ''}`);
      else toast.success(skipped ? `Already up to date (${skipped} existing)` : 'No holidays found to import');
      if (failed.length) toast.error(`Couldn't fetch holidays for ${failed.join(', ')}`);
      afterChange();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to import holidays');
    } finally {
      setImporting(null);
    }
  }

  async function handleMakeDefault() {
    const ok = await confirm({
      title: `Make ${location.name} the default?`,
      message: 'New people, and anyone not assigned to a location, will get these public holidays.',
      confirmLabel: 'Make default',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiService.setDefaultHolidayLocation(location.id);
      toast.success('Default location updated');
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update default');
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteLocation() {
    const ok = await confirm({
      title: `Delete ${location.name}?`,
      message: 'All of its holidays will be removed. People in this location will move to the default location.',
      confirmLabel: 'Delete location',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiService.deleteHolidayLocation(location.id);
      toast.success('Location deleted');
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete location');
      setBusy(false);
    }
  }

  const defaultNewDate = year === CURRENT_YEAR ? todayISO() : `${year}-01-01`;

  return (
    <>
      <Modal onClose={onClose} className="max-w-4xl sm:h-[min(780px,92vh)]" z={200} labelledBy="ph-location-title">
        {/* Header */}
        <div className="flex items-start gap-4 px-5 pt-5 sm:px-6">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sand-50 text-2xl ring-1 ring-sand-200">
            {flagEmoji(location.countryCode)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 id="ph-location-title" className="m-0 truncate text-lg font-semibold tracking-tight text-sand-900">{country}</h3>
              {location.isDefault && <DefaultBadge />}
            </div>
            <p className="m-0 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-sand-600">
              <span className="inline-flex items-center gap-1"><MapPin size={12} />{region || 'National holidays'}</span>
              <span className="inline-flex items-center gap-1"><Users size={12} />{location.peopleCount || 0} {location.peopleCount === 1 ? 'person' : 'people'}</span>
            </p>
          </div>
          <div className="flex items-center gap-1">
            {!location.isDefault && (
              <Menu
                trigger={({ toggle }) => (
                  <IconButton label="More actions" onClick={toggle} disabled={busy}><MoreHorizontal size={17} /></IconButton>
                )}
                items={[
                  { label: 'Make default', hint: 'Used by anyone without a location', icon: Star, onClick: handleMakeDefault },
                  { label: 'Delete location', hint: 'Removes all of its holidays', icon: Trash2, tone: 'danger', onClick: handleDeleteLocation },
                ]}
              />
            )}
            <IconButton label="Close" onClick={onClose}><X size={17} /></IconButton>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-4 flex gap-6 border-b border-sand-100 px-5 sm:px-6" role="tablist">
          {[
            { key: 'holidays', label: 'Holidays', icon: CalendarDays },
            { key: 'people', label: 'People', icon: Users, count: location.peopleCount },
          ].map(t => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={cx(
                '-mb-px inline-flex items-center gap-2 border-b-2 pb-3 text-sm font-medium transition-colors',
                tab === t.key ? 'border-ink text-ink' : 'border-transparent text-sand-600 hover:text-sand-900',
              )}
            >
              <t.icon size={15} />
              {t.label}
              {!!t.count && <span className="rounded-full bg-sand-100 px-1.5 text-[11px] font-semibold text-sand-600">{t.count}</span>}
            </button>
          ))}
        </div>

        {tab === 'people' ? (
          <PeoplePanel location={location} locations={locations} onChanged={onChanged} />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            {/* Toolbar */}
            <div className="space-y-3 px-5 pb-3 pt-4 sm:px-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="-mx-1 overflow-x-auto px-1">
                  <Segmented
                    value={year}
                    onChange={setYear}
                    options={YEARS.map(y => ({ value: y, label: String(y), count: y === year && !loading ? holidays.length : undefined }))}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Segmented
                    value={view}
                    onChange={setView}
                    options={[
                      { value: 'list', label: '', icon: List, title: 'List view' },
                      { value: 'calendar', label: '', icon: LayoutGrid, title: 'Calendar view' },
                    ]}
                  />
                  <Menu
                    trigger={({ toggle, open }) => (
                      <Button size="sm" icon={importing ? undefined : Download} loading={!!importing} onClick={toggle}>
                        Import <ChevronDown size={13} className={cx('transition-transform', open && 'rotate-180')} />
                      </Button>
                    )}
                    items={[
                      { label: `Import ${year}`, hint: 'Adds any missing official dates', icon: RefreshCw, onClick: () => handleImport('year') },
                      { label: 'Import all years', hint: YEAR_RANGE, icon: Download, onClick: () => handleImport('all') },
                    ]}
                  />
                  <Button size="sm" variant="primary" icon={Plus} onClick={() => setEditor({ date: defaultNewDate })}>Add date</Button>
                </div>
              </div>

              {view === 'list' && holidays.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <SearchInput value={search} onChange={setSearch} placeholder="Search holidays…" className="w-full sm:w-64" />
                  <span className="text-[13px] text-sand-600">
                    <span className="font-semibold text-sand-900">{holidays.length}</span> holidays ·{' '}
                    <span className="font-semibold text-sand-900">{weekdayCount}</span> on working days
                  </span>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-5 sm:px-4">
              {loading ? (
                <div className="space-y-2 px-2 pt-1">
                  {[0, 1, 2, 3, 4].map(i => <div key={i} className="h-14 animate-pulse rounded-xl bg-sand-50" />)}
                </div>
              ) : holidays.length === 0 ? (
                <EmptyHolidays
                  year={year}
                  importing={importing}
                  onImportYear={() => handleImport('year')}
                  onImportAll={() => handleImport('all')}
                  onAdd={() => setEditor({ date: defaultNewDate })}
                />
              ) : view === 'calendar' ? (
                <YearCalendar
                  year={year}
                  holidays={holidays}
                  onPick={(iso, holiday) => setEditor(holiday ? { holiday } : { date: iso })}
                />
              ) : (
                <HolidayList
                  holidays={holidays}
                  search={search}
                  nextId={nextId}
                  onEdit={holiday => setEditor({ holiday })}
                  onDelete={handleDelete}
                />
              )}
            </div>
          </div>
        )}
      </Modal>

      {editor && (
        <HolidayFormModal
          locationId={location.id}
          holiday={editor.holiday}
          defaultDate={editor.date}
          existing={holidays}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); afterChange(); }}
          onDelete={editor.holiday ? () => { const h = editor.holiday; setEditor(null); handleDelete(h); } : undefined}
        />
      )}
    </>
  );
}

function EmptyHolidays({ year, importing, onImportYear, onImportAll, onAdd }) {
  return (
    <div className="ph-fade flex flex-col items-center px-6 py-14 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-ink-50 text-ink">
        <CalendarDays size={28} />
      </span>
      <h4 className="m-0 mt-4 text-base font-semibold text-sand-900">No holidays for {year} yet</h4>
      <p className="m-0 mt-1 max-w-sm text-sm text-sand-600">
        Import the official public holidays for this location, or add your own dates.
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        <Button variant="primary" icon={Download} loading={importing === 'all'} disabled={!!importing} onClick={onImportAll}>
          Import {YEAR_RANGE}
        </Button>
        <Button icon={RefreshCw} loading={importing === 'year'} disabled={!!importing} onClick={onImportYear}>
          Just {year}
        </Button>
        <Button variant="ghost" icon={Plus} onClick={onAdd}>Add manually</Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// List view
// ─────────────────────────────────────────────────────────────
function HolidayList({ holidays, search, nextId, onEdit, onDelete }) {
  const q = search.trim().toLowerCase();
  const visible = q ? holidays.filter(h => h.name.toLowerCase().includes(q)) : holidays;

  const groups = useMemo(() => {
    const byMonth = new Map();
    visible.forEach(h => {
      const m = parseISO(h.date).getMonth();
      if (!byMonth.has(m)) byMonth.set(m, []);
      byMonth.get(m).push(h);
    });
    return [...byMonth.entries()].sort((a, b) => a[0] - b[0]);
  }, [visible]);

  if (!visible.length) {
    return <div className="py-12 text-center text-sm text-sand-500">No holidays match “{search}”</div>;
  }

  return (
    <div className="space-y-4 pt-1">
      {groups.map(([month, items]) => (
        <section key={month}>
          <div className="sticky top-0 z-10 bg-[color:var(--surface)] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-sand-500 backdrop-blur">
            {MONTHS[month]} <span className="font-medium normal-case tracking-normal text-sand-500">· {items.length}</span>
          </div>
          <div className="space-y-0.5">
            {items.map(h => (
              <HolidayRow key={h.id} holiday={h} isNext={h.id === nextId} onEdit={() => onEdit(h)} onDelete={() => onDelete(h)} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function HolidayRow({ holiday, isNext, onEdit, onDelete }) {
  const iso = isoOf(holiday.date);
  const d = parseISO(iso);
  const past = daysFromToday(iso) < 0;
  const weekend = isWeekend(iso);

  return (
    <div
      className={cx(
        'group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors sm:gap-4',
        isNext ? 'bg-[color:var(--ok-bg)] hover:bg-[color:var(--ok-bg)]' : 'hover:bg-sand-50',
      )}
    >
      <button
        type="button"
        onClick={onEdit}
        className={cx(
          'flex h-12 w-12 shrink-0 flex-col items-center justify-center overflow-hidden rounded-xl border text-center transition-transform group-hover:scale-105',
          isNext ? 'border-emerald-600 bg-emerald-600 text-white' : past ? 'border-sand-200 bg-sand-50 text-sand-500' : 'border-sand-200 bg-[color:var(--surface)] text-sand-900',
        )}
        aria-label={`Edit ${holiday.name}`}
      >
        <span className={cx('text-[10px] font-semibold uppercase tracking-wider', isNext ? 'text-white/80' : 'text-sand-500')}>
          {MONTHS[d.getMonth()].slice(0, 3)}
        </span>
        <span className="text-lg font-bold leading-none">{d.getDate()}</span>
      </button>

      <button type="button" onClick={onEdit} className="min-w-0 flex-1 text-left">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className={cx('truncate text-sm font-medium', past ? 'text-sand-500' : 'text-sand-900')}>{holiday.name}</span>
          {isNext && <Chip tone="green">Next up</Chip>}
          {weekend && <Chip tone="amber" title="Falls on a weekend, so it doesn't reduce working days">Weekend</Chip>}
        </span>
        <span className="mt-0.5 block text-xs text-sand-500">
          {weekdayLong(iso)} · {past ? 'Passed' : relativeLabel(iso)}
        </span>
      </button>

      <div className="flex gap-0.5 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
        <IconButton label="Edit" onClick={onEdit}><Edit2 size={14} /></IconButton>
        <IconButton label="Delete" tone="danger" onClick={onDelete}><Trash2 size={14} /></IconButton>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Calendar view
// ─────────────────────────────────────────────────────────────
function YearCalendar({ year, holidays, onPick }) {
  const byDate = useMemo(() => {
    const map = new Map();
    holidays.forEach(h => {
      const iso = isoOf(h.date);
      if (!map.has(iso)) map.set(iso, []);
      map.get(iso).push(h);
    });
    return map;
  }, [holidays]);

  const today = todayISO();

  return (
    <div className="ph-fade px-2 pt-1">
      <div className="mb-3 flex flex-wrap items-center gap-4 px-1 text-xs text-sand-600">
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-ink" /> Public holiday</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded ring-2 ring-inset ring-emerald-500" /> Today</span>
        <span className="text-sand-500">Click a date to add a holiday, or a holiday to edit it.</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {MONTHS.map((name, month) => (
          <MonthGrid key={name} year={year} month={month} name={name} byDate={byDate} today={today} onPick={onPick} />
        ))}
      </div>
    </div>
  );
}

function MonthGrid({ year, month, name, byDate, today, onPick }) {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const monthCount = [...byDate.keys()].filter(iso => iso.startsWith(`${year}-${pad(month + 1)}-`)).length;

  return (
    <div className="rounded-2xl border border-sand-200 p-3">
      <div className="mb-2 flex items-center justify-between px-1">
        <span className="text-sm font-semibold text-sand-900">{name}</span>
        {monthCount > 0 && <Chip tone="ink">{monthCount}</Chip>}
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {WEEKDAYS.map(w => (
          <span key={w} className="pb-1 text-[10px] font-semibold uppercase text-sand-500">{w.slice(0, 2)}</span>
        ))}
        {cells.map((day, i) => {
          if (!day) return <span key={`b${i}`} />;
          const iso = `${year}-${pad(month + 1)}-${pad(day)}`;
          const hols = byDate.get(iso);
          const weekend = i % 7 >= 5;
          const isToday = iso === today;
          return (
            <button
              key={iso}
              type="button"
              onClick={() => onPick(iso, hols?.[0])}
              title={hols ? hols.map(h => h.name).join(', ') : `Add a holiday on ${fmtShort(iso)}`}
              className={cx(
                'relative flex aspect-square items-center justify-center rounded-lg text-xs transition-all',
                hols
                  ? 'bg-ink font-semibold text-[color:var(--on-accent)] shadow-sm hover:scale-110 hover:shadow'
                  : cx('hover:bg-sand-100', weekend ? 'text-sand-500' : 'text-sand-700'),
                isToday && 'ring-2 ring-inset ring-emerald-500',
              )}
            >
              {day}
              {hols && hols.length > 1 && <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-amber-400" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// PANEL: People
// ═════════════════════════════════════════════════════════════
function PeoplePanel({ location, locations = [], onChanged }) {
  const [users, setUsers]       = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('all');

  const otherLocations  = useMemo(() => locations.filter(l => l.id !== location.id), [locations, location.id]);
  const defaultLocation = useMemo(() => locations.find(l => l.isDefault), [locations]);
  const locById         = useMemo(() => Object.fromEntries(locations.map(l => [l.id, l])), [locations]);
  const fallbackMoveTo  = location.isDefault ? (otherLocations[0]?.id ?? '') : (defaultLocation?.id ?? '');
  const [moveTo, setMoveTo] = useState(fallbackMoveTo);

  useEffect(() => {
    if (!otherLocations.some(l => l.id === moveTo)) setMoveTo(fallbackMoveTo);
  }, [otherLocations, moveTo, fallbackMoveTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = unwrapList(await apiService.getHolidayLocationMembers(location.id), 'users');
      setUsers(list);
      setSelected(new Set(list.filter(u => u.assigned).map(u => u.id)));
    } catch {
      toast.error('Failed to load people');
    } finally {
      setLoading(false);
    }
  }, [location.id]);

  useEffect(() => { load(); }, [load]);

  const original = useMemo(() => new Set(users.filter(u => u.assigned).map(u => u.id)), [users]);

  const addedCount   = useMemo(() => [...selected].filter(id => !original.has(id)).length, [selected, original]);
  const removedCount = useMemo(() => [...original].filter(id => !selected.has(id)).length, [selected, original]);
  const dirty        = addedCount + removedCount > 0;
  const cannotRemove = removedCount > 0 && otherLocations.length === 0;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      if (filter === 'here' && !original.has(u.id)) return false;
      if (filter === 'elsewhere' && original.has(u.id)) return false;
      return !q || `${fullName(u)} ${u.email ?? ''}`.toLowerCase().includes(q);
    });
  }, [users, search, filter, original]);

  const allVisibleSelected = filtered.length > 0 && filtered.every(u => selected.has(u.id));

  const toggle = id => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleVisible = () => setSelected(prev => {
    const next = new Set(prev);
    filtered.forEach(u => (allVisibleSelected ? next.delete(u.id) : next.add(u.id)));
    return next;
  });

  async function handleSave() {
    if (cannotRemove) return;
    if (removedCount > 0 && !moveTo) { toast.error('Choose where to move the removed people'); return; }
    setSaving(true);
    try {
      await apiService.setHolidayLocationMembers(location.id, [...selected], removedCount > 0 ? moveTo : null);
      toast.success('People updated');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update people');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 px-5 pb-3 pt-4 sm:px-6">
        <SearchInput value={search} onChange={setSearch} placeholder="Search people…" className="min-w-[200px] flex-1" />
        <Segmented
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'All', count: users.length },
            { value: 'here', label: 'Here', count: original.size },
            { value: 'elsewhere', label: 'Elsewhere', count: users.length - original.size },
          ]}
        />
      </div>

      {/* List */}
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 sm:px-4">
        {loading ? (
          <div className="space-y-2 px-2">
            {[0, 1, 2, 3].map(i => <div key={i} className="h-12 animate-pulse rounded-xl bg-sand-50" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-sand-500">No people found</div>
        ) : (
          <>
            <button
              type="button"
              onClick={toggleVisible}
              className="mb-1 ml-3 text-[13px] font-medium text-ink hover:underline"
            >
              {allVisibleSelected ? 'Untick' : 'Tick'} all {filtered.length} shown
            </button>
            <div className="space-y-0.5">
              {filtered.map(u => {
                const isSelected = selected.has(u.id);
                const wasHere = original.has(u.id);
                const adding = isSelected && !wasHere;
                const removing = !isSelected && wasHere;
                const current = locById[u.currentLocationId];
                return (
                  <label
                    key={u.id}
                    className={cx(
                      'flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 transition-colors',
                      adding ? 'bg-[color:var(--ok-bg)]' : removing ? 'bg-[color:var(--bad-bg)]' : 'hover:bg-sand-50',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={saving}
                      onChange={() => toggle(u.id)}
                      className="h-4 w-4 shrink-0 cursor-pointer accent-ink"
                    />
                    <Avatar person={u} size={34} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-sand-900">{fullName(u)}</span>
                      {u.email && <span className="block truncate text-xs text-sand-500">{u.email}</span>}
                    </span>
                    {adding ? (
                      <Chip tone="green"><Plus size={10} /> Will be added</Chip>
                    ) : removing ? (
                      <Chip tone="red"><X size={10} /> Will be removed</Chip>
                    ) : wasHere ? (
                      <Chip tone="ink"><Check size={10} /> Here</Chip>
                    ) : current ? (
                      <Chip tone="sand" title={current.name}>
                        {flagEmoji(current.countryCode)} <span className="max-w-[120px] truncate">{splitLocationName(current.name).region || splitLocationName(current.name).country}</span>
                      </Chip>
                    ) : null}
                  </label>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className={cx('border-t px-5 py-3 transition-colors sm:px-6', dirty ? 'border-ink/10 bg-ink-50/50' : 'border-sand-100')}>
        {removedCount > 0 && (
          <div className="mb-3">
            {cannotRemove ? (
              <div className="flex items-center gap-2 text-[13px] text-[color:var(--bad-text)]">
                <AlertTriangle size={14} className="shrink-0" />
                This is your only location. Add another one before removing people from it.
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <span className="whitespace-nowrap text-[13px] text-sand-700">
                  Move {removedCount} removed {removedCount === 1 ? 'person' : 'people'} to
                </span>
                <select
                  value={moveTo}
                  onChange={e => setMoveTo(e.target.value)}
                  disabled={saving}
                  className={cx(selectClass, 'h-9 w-auto min-w-[220px] flex-1')}
                >
                  {otherLocations.map(l => (
                    <option key={l.id} value={l.id}>
                      {flagEmoji(l.countryCode)}  {l.name}{l.isDefault ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-[13px] text-sand-600">
            {dirty ? (
              <>
                {addedCount > 0 && <span className="font-semibold text-[color:var(--ok-text)]">+{addedCount} added</span>}
                {addedCount > 0 && removedCount > 0 && ' · '}
                {removedCount > 0 && <span className="font-semibold text-[color:var(--bad-text)]">−{removedCount} removed</span>}
              </>
            ) : 'No unsaved changes'}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" disabled={!dirty || saving} onClick={() => setSelected(new Set(original))}>Reset</Button>
            <Button size="sm" variant="primary" icon={Check} loading={saving} disabled={!dirty || cannotRemove} onClick={handleSave}>
              Save people
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// MODAL: Add / edit holiday
// ═════════════════════════════════════════════════════════════
function HolidayFormModal({ locationId, holiday, defaultDate, existing = [], onClose, onSaved, onDelete }) {
  const [name, setName]     = useState(holiday?.name || '');
  const [date, setDate]     = useState(isoOf(holiday?.date || defaultDate));
  const [saving, setSaving] = useState(false);

  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date);
  const clash = validDate ? existing.find(h => isoOf(h.date) === date && h.id !== holiday?.id) : null;
  const canSave = name.trim() && validDate && !clash;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      if (holiday) {
        await apiService.updateLocationHoliday(locationId, holiday.id, { name: name.trim(), date });
        toast.success('Holiday updated');
      } else {
        await apiService.createLocationHoliday(locationId, { name: name.trim(), date });
        toast.success('Holiday added');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save holiday');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} className="max-w-md" z={400} labelledBy="ph-holiday-title">
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
        <ModalHeader
          id="ph-holiday-title"
          title={holiday ? 'Edit holiday' : 'Add a holiday'}
          subtitle={holiday ? 'Changes apply to everyone in this location' : 'Add a company day off or a missing date'}
          onClose={onClose}
          leading={
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ink-50 text-ink">
              <CalendarDays size={19} />
            </span>
          }
        />

        <div className="space-y-4 px-5 pb-2 pt-3 sm:px-6">
          <div>
            <label htmlFor="ph-holiday-name" className={labelClass}>Name</label>
            <input
              id="ph-holiday-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Company away day"
              autoFocus
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="ph-holiday-date" className={labelClass}>Date</label>
            <input
              id="ph-holiday-date"
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className={inputClass}
            />
          </div>

          {validDate && (
            <div
              className={cx(
                'ph-fade flex items-start gap-2.5 rounded-xl px-3 py-2.5 text-[13px]',
                clash ? 'bg-[color:var(--bad-bg)] text-[color:var(--bad-text)]' : isWeekend(date) ? 'bg-[color:var(--warn-bg)] text-[color:var(--warn-text)]' : 'bg-sand-50 text-sand-700',
              )}
            >
              {clash || isWeekend(date) ? <AlertTriangle size={15} className="mt-px shrink-0" /> : <CalendarDays size={15} className="mt-px shrink-0" />}
              <span>
                <span className="block font-semibold">{fmtLong(date)}</span>
                {clash
                  ? <>Already a holiday: {clash.name}</>
                  : isWeekend(date)
                    ? 'This is a weekend, so it won’t change anyone’s working days.'
                    : relativeLabel(date)}
              </span>
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center gap-2 border-t border-sand-100 px-5 py-4 sm:px-6">
          {onDelete && (
            <Button variant="ghost" icon={Trash2} className="text-[color:var(--bad-text)] hover:bg-[color:var(--bad-bg)] hover:text-[color:var(--bad-text)]" onClick={onDelete} disabled={saving}>
              Delete
            </Button>
          )}
          <div className="ml-auto flex gap-2">
            <Button onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" variant="primary" icon={Check} loading={saving} disabled={!canSave}>
              {holiday ? 'Save changes' : 'Add holiday'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default PublicHolidaysPage;